/**
 * @features/dashboard/config/permissions.js
 * -----------------------------------------
 * Declarative permission matrix. ONE source of truth for "can <persona>
 * do <action>?" across the entire frontend. Mirrored on the backend at
 * `/app/backend/permissions.py` (same persona keys, same permission
 * strings — keep them in sync).
 *
 * Why this file (not hardcoded role checks scattered across components)?
 *   • Adding a role, action, or capability is a single config edit.
 *   • Both UI guards (`<Guard>` / `useCan`) and request validation pull
 *     from the same vocabulary — no drift between FE and BE.
 *   • Renames stay grep-able.
 *
 * Personas
 * --------
 *   anon         – not logged in
 *   buyer        – role=client (default)
 *   owner        – role=client with `owner_intent=true` (reserved)
 *   dealer       – role=dealer
 *   builder      – role=builder
 *   caller       – role=team / team_member
 *   field_agent  – role=team with `field_agent=true` (reserved)
 *   admin        – role=admin (wildcard via `admin:*`)
 *
 * Permission codes — `<resource>:<action>[-scope]`
 * ------------------------------------------------
 * Resources : property · project · lead · enquiry · shortlist · plan ·
 *             user · content · analytics · field · contact · map · admin
 *
 * Actions (the 9 categories from product spec) :
 *   • view         — read access ("*:view")
 *   • create       — make a new record
 *   • edit         — mutate an existing record (-own / -any scope)
 *   • delete       — remove a record (-own / -any scope)
 *   • assign       — re-route ownership (e.g. lead:assign)
 *   • export       — bulk download (CSV/PDF)
 *   • contact:view — see phone/email of a person
 *   • analytics:*  — see aggregated metrics
 *   • lead:view*   — visibility scope (own vs all)
 *
 * v2.71.27 — Initial extraction.
 * v2.71.41 — Centralized scalable role-permission architecture. Matrix
 *            now spans all 9 product-spec categories; backend mirror
 *            added; `<Guard>` + `require_perm()` consume same vocabulary.
 */

/** Platform-level roles (string values match `users.role` in MongoDB). */
export const PLATFORM_ROLE = {
  ADMIN: "admin",
  DEALER: "dealer",
  BUILDER: "builder",
  TEAM: "team",
  CLIENT: "client",
  ANON: "anon",
};

/**
 * Resolve the persona for a user record. Personas are UX labels that
 * map onto platform roles, possibly refined by profile flags.
 */
export function resolvePersona(user) {
  if (!user) return "anon";
  switch (user.role) {
    case "admin":
      return "admin";
    case "dealer":
      return "dealer";
    case "builder":
      return "builder";
    case "team":
    case "team_member": {
      // Field-agent persona triggers on EITHER explicit flag (`field_agent: true`)
      // OR the team_member `sub_role === "agent"` set by admin during onboarding.
      const isFieldAgent = user.field_agent === true || user.sub_role === "agent";
      return isFieldAgent ? "field_agent" : "caller";
    }
    case "client":
    default:
      return user.owner_intent ? "owner" : "buyer";
  }
}

/**
 * Permission matrix — { persona: Set<permission> }.
 *
 * Admins inherit everything via the `admin:*` wildcard.
 * Other personas list only the permissions they explicitly need.
 * Missing = denied. Inheritance is declared in `INHERITS` below.
 *
 * Adding a new capability:
 *   1. Pick a `resource:action[-scope]` code (kebab-case after colon).
 *   2. Add it to each persona that should have it.
 *   3. Mirror in `/app/backend/permissions.py`.
 *   4. Gate UI with `<Guard perm="...">` and backend with
 *      `Depends(require_perm("..."))`.
 */
const PERMISSIONS = {
  anon: new Set([
    "dashboard:view-public",
    "property:browse",
  ]),

  buyer: new Set([
    "dashboard:view",
    "property:browse",
    "property:save",
    "shortlist:view",
    "plan:purchase",
    "enquiry:create",
    "enquiry:view-own",
    "alert:create",
    "alert:view-own",
  ]),

  owner: new Set([
    // Inherits buyer (resolved at lookup time).
    "property:create",
    "property:edit-own",
    "property:delete-own",
    "lead:view-own",
    "contact:view-own",
  ]),

  dealer: new Set([
    "dashboard:view",
    "property:browse",
    "property:create",
    "property:edit-own",
    "property:delete-own",
    "property:save",
    "shortlist:create",
    "shortlist:view",
    "plan:purchase",
    "lead:view-own",
    "lead:respond",
    "lead:disposition",
    "enquiry:view-own",
    "contact:view-own",        // can see phone of leads on OWN listings
    "map:view-coords",
    "analytics:view-own",
  ]),

  builder: new Set([
    "dashboard:view",
    "property:browse",
    "property:create",
    "property:edit-own",
    "property:delete-own",
    "project:create",
    "project:edit-own",
    "project:delete-own",
    "plan:purchase",
    "lead:view-own",
    "lead:respond",
    "lead:disposition",
    "enquiry:view-own",
    "contact:view-own",
    "shortlist:create",
    "shortlist:view",
    "map:view-coords",
    "analytics:view-own",
  ]),

  caller: new Set([
    "dashboard:view",
    "lead:view",                // all leads (assigned + queue)
    "lead:respond",
    "lead:disposition",
    "enquiry:view",
    "shortlist:create",
    "shortlist:view",
    "shortlist:share",
    "property:browse",
    "contact:view",             // can see customer phone (call workflow)
    "map:view-coords",
  ]),

  field_agent: new Set([
    // Inherits caller. Field-specific additions.
    "field:visit-log",
    "field:nav-to-property",
    "contact:view",
  ]),

  admin: new Set([
    // Admin gets EVERYTHING via the wildcard. Listed for explicitness:
    "admin:*",
    "property:approve",
    "property:edit-any",
    "property:delete-any",
    "property:assign",
    "property:export",
    "project:approve",
    "project:edit-any",
    "project:delete-any",
    "lead:view",
    "lead:assign",
    "lead:delete",
    "lead:export",
    "enquiry:view",
    "enquiry:export",
    "user:view",
    "user:edit",
    "user:delete",
    "user:block",
    "user:impersonate",
    "user:export",
    "plan:manage",
    "content:cms-edit",
    "content:plans-edit",
    "shortlist:edit-any",
    "shortlist:delete-any",
    "analytics:view",
    "analytics:view-advanced",
    "analytics:export",
    "contact:view",
    "transaction:view",
    "transaction:refund",
    "transaction:export",
  ]),
};

/** Personas that inherit from another. Resolved at lookup time. */
const INHERITS = {
  owner: "buyer",
  field_agent: "caller",
};

/** Internal recursive permission check. */
function _hasPerm(persona, perm) {
  const own = PERMISSIONS[persona];
  if (!own) return false;
  if (own.has(perm)) return true;
  if (own.has("admin:*")) return true; // admin wildcard
  const parent = INHERITS[persona];
  if (parent) return _hasPerm(parent, perm);
  return false;
}

/**
 * Top-level permission check used by `useCan` + `<Guard>`.
 *
 * @param {Object|null} user  Full user record from AuthContext.
 * @param {string} perm       Colon-namespaced permission code.
 * @returns {boolean}
 */
export function can(user, perm) {
  // v2.71.44 — Honor per-user `perm_overrides` first
  // (admin-grant-able grants and revokes beat role defaults).
  if (user && user.perm_overrides) {
    const revoked = user.perm_overrides.revoked || [];
    if (revoked.includes(perm)) return false;
    const granted = user.perm_overrides.granted || [];
    if (granted.includes(perm)) return true;
  }
  return _hasPerm(resolvePersona(user), perm);
}

/**
 * Bulk check — returns true if user has ANY of the listed permissions.
 * Useful for guarded sections where multiple perms can unlock the UI.
 */
export function canAny(user, perms) {
  const persona = resolvePersona(user);
  return (perms || []).some((p) => _hasPerm(persona, p));
}

/**
 * Bulk check — returns true if user has ALL of the listed permissions.
 */
export function canAll(user, perms) {
  const persona = resolvePersona(user);
  return (perms || []).every((p) => _hasPerm(persona, p));
}

/** Returns the list of permissions a user has (debugging / audit). */
export function listPermissions(user) {
  const persona = resolvePersona(user);
  const collected = new Set();
  const visit = (p) => {
    const own = PERMISSIONS[p];
    if (!own) return;
    own.forEach((x) => collected.add(x));
    const parent = INHERITS[p];
    if (parent) visit(parent);
  };
  visit(persona);
  return Array.from(collected).sort();
}

/** Exported for backend mirror tests / introspection. */
export const _PERMISSION_MATRIX = PERMISSIONS;
export const _PERSONA_INHERITS = INHERITS;
